import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { AlertDialog, ImageLightbox } from '@utriper/core-ui';
import { Button, FormHelperText, Grid2 } from '@mui/material';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Add } from '@mui/icons-material';
import GalleryItem from './components/GalleryItem';
import AddMultimediaForm from './components/AddMultimediaForm';
import SortableGalleryItem from './components/SortableGalleryItem';
import { Container, Label } from 'modules/Experience/ExperienceWizard/styles';

const EmptyImage = ({ size = 3 }) => {
    return <Grid2 size={size} sx={{ aspectRatio: '1/1', borderRadius: '4px', backgroundColor: '#13131326' }}></Grid2>;
};

const MultimediaFormFields = ({ form, cardSize = 3, maxEmpty = 8, hideLabel = false }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const { values } = useFormState();
    const [open, setOpen] = useState(false);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [initialIndex, setInitialIndex] = useState(null);

    const handleOpenLightbox = (newIndex) => {
        setInitialIndex(newIndex);
        setOpenLightbox(true);
    };

    const handleCloseAddForm = () => setOpen(false);

    const handleCloseLightbox = () => {
        setOpenLightbox(false);
        setInitialIndex(null);
    };

    const handleAddMultimedia = async ({ file, videoId, isVideo = false }) => {
        const prevValues = values?.images || [];
        let newImage = [];
        if (!isVideo) {
            if (!Array.isArray(file)) {
                file = [file];
            }
            for (const f of file) {
                if (!f?.name) return; // TODO: ADD ERROR MESSAGE
                const src = URL.createObjectURL(f);

                newImage.push({
                    src,
                    type: 1, // type image, todo: add type in core
                    file: f,
                });

                await form.change('images', [...prevValues, newImage]);
            }
        } else if (videoId && isVideo) {
            newImage.push({
                src: videoId,
                videoId,
                type: 3, // type YouTube, todo: add type in core
                isVideo,
                alt: ' ',
            });
        }

        form.change('images', [...prevValues, ...newImage]);
    };

    return (
        <React.Fragment>
            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                <Container>
                    {!hideLabel && <Label>{translate('gallery')}</Label>}
                    <FieldArray name="images">
                        {({ fields }) => {
                            const handleDragEnd = (event) => {
                                const { active, over } = event;
                                if (!over) return;

                                if (active.id !== over.id) {
                                    fields.move(active.id, over.id);
                                }
                            };

                            return (
                                <Grid2 container size={12} spacing={1} alignItems="center">
                                    {values?.images?.length > 0 && (
                                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                            <SortableContext items={values.images.map((_, i) => i)} strategy={rectSortingStrategy}>
                                                {fields.map((name, index) => {
                                                    const image = values.images[index];

                                                    return (
                                                        <SortableGalleryItem key={name} id={index} size={cardSize}>
                                                            <GalleryItem image={image} onRemove={() => fields.remove(index)} onOpen={() => handleOpenLightbox(index)} />
                                                        </SortableGalleryItem>
                                                    );
                                                })}
                                            </SortableContext>
                                        </DndContext>
                                    )}

                                    {/* Empty slots */}
                                    {fields.length < maxEmpty && Array.from({ length: maxEmpty - fields.length }).map((_, index) => <EmptyImage key={`empty-${index}`} size={cardSize} />)}
                                </Grid2>
                            );
                        }}
                    </FieldArray>
                </Container>
                <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => setOpen(true)}>
                    {translate('add_multimedia')}
                </Button>
                <FormHelperText>{translate('multimedia_helper')}</FormHelperText>
            </Grid2>

            {/* Add Dialog */}
            <AlertDialog open={open} handleClose={handleCloseAddForm} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<AddMultimediaForm onAddMultimedia={handleAddMultimedia} onClose={handleCloseAddForm} />} />
            {/* Image Ligthbox */}
            <ImageLightbox open={openLightbox} initialIndex={initialIndex} handleClose={handleCloseLightbox} images={values?.images ?? []} multiple={values?.images?.length > 1} />
        </React.Fragment>
    );
};

export default MultimediaFormFields;
